// Lets the flavor picker on the PDP switch to a sibling product (a different
// flavor is always a different product in this catalog) without a full page
// reload. Rather than re-implementing snippets/price.liquid's discount logic
// (or the sweetness/coffee-notes scale math) in JS, this fetches the target
// product's own rendered section markup and patches only the essential
// pieces (title, gallery, variant id inputs, price blocks, flavor sliders,
// active state) into the current DOM.

(function () {
  // `liveScope: 'document'` is for pieces that live inside a <modal-dialog>:
  // that custom element moves itself to be a direct child of <body> as soon
  // as it connects to the live DOM (see ModalDialog.connectedCallback in
  // global.js), so by the time this script runs it's no longer inside the
  // section root and has to be looked up document-wide instead. The fetched
  // HTML was only ever parsed (never inserted into a live document), so no
  // custom element upgraded there — it stays put inside fetchedRoot either way.
  var SYNC_SELECTORS = [
    { selector: '.pdp-gallery' },
    { selector: '.pdp-rating-trigger' },
    { selector: '.pdp-title' },
    { selector: '.pdp-price-main' },
    { selector: '.subscription_price-container' },
    { selector: '.pdp-price-info-row' },
    { selector: '.pdp-price-onetime' },
    { selector: '.pdp-sliders' },
    // Name + custom.flavor_text description ("Caramel Machiatto · Rich and
    // indulgent") — synced whole so setActiveCard()'s manual name update
    // below is just a harmless redundant overwrite of the same value.
    { selector: '.pdp-flavor-current' },
    { selector: '.pdp-nutrition-trigger' },
    // Both the image (custom.nutrition_label_image) and the caption's
    // "Panel shown: <flavor> · <format>" prefix are per-product.
    { selector: '.pdp-nutrition-modal__image', liveScope: 'document', modalId: 'NutritionLabelModal' },
    { selector: '.pdp-nutrition-modal__caption', liveScope: 'document', modalId: 'NutritionLabelModal' },
    // "One Time Purchase · $X" — plain static text + price, safe to swap whole.
    { selector: '#gifts-buy-once' },
    // #add-to-cart-copy's text ("Subscribe"/"Add to cart") is owned by JS
    // (updateAddToCartCopy in main-product.js), so only the price data
    // attributes are synced here — re-applied via updateAddToCartCopy() below,
    // instead of overwriting innerHTML and losing the current toggle state.
    { selector: '#add-to-cart-copy', syncDataAttrs: ['data-onetime-price', 'data-subscribe-price'] },
  ];

  function closestFlavorLink(target) {
    return target.closest('[data-pdp-flavor-switch]');
  }

  function setLoading(root, isLoading) {
    var grid = root.querySelector('.pdp-flavor-grid');
    if (grid) grid.classList.toggle('pdp-flavor-grid--loading', isLoading);
  }

  function syncAttributes(liveEl, fetchedEl, entry) {
    if (entry && entry.syncDataAttrs) {
      entry.syncDataAttrs.forEach(function (attr) {
        if (fetchedEl.hasAttribute(attr)) {
          liveEl.setAttribute(attr, fetchedEl.getAttribute(attr));
        } else {
          liveEl.removeAttribute(attr);
        }
      });
      return;
    }
    if (liveEl.tagName === 'IMG') {
      liveEl.src = fetchedEl.src;
      if (fetchedEl.dataset.fullSrc) liveEl.dataset.fullSrc = fetchedEl.dataset.fullSrc;
      liveEl.alt = fetchedEl.alt;
      return;
    }
    if (liveEl.tagName === 'INPUT') {
      liveEl.value = fetchedEl.value;
      return;
    }
    liveEl.innerHTML = fetchedEl.innerHTML;
  }

  function patchSection(root, fetchedRoot, sectionId) {
    SYNC_SELECTORS.forEach(function (entry) {
      var liveEl;
      if (entry.liveScope === 'document') {
        liveEl = document.querySelector('#' + entry.modalId + '-' + sectionId + ' ' + entry.selector);
      } else {
        liveEl = root.querySelector(entry.selector);
      }
      var fetchedEl = fetchedRoot.querySelector(entry.selector);
      if (liveEl && fetchedEl) syncAttributes(liveEl, fetchedEl, entry);
    });

    var liveVariantInputs = root.querySelectorAll('input[name="id"]');
    var fetchedVariantInputs = fetchedRoot.querySelectorAll('input[name="id"]');
    liveVariantInputs.forEach(function (input, index) {
      var fetchedInput = fetchedVariantInputs[index];
      if (fetchedInput) input.value = fetchedInput.value;
    });
  }

  function setActiveCard(root, clickedCard, flavorName) {
    root.querySelectorAll('.pdp-flavor-card.flavor_active').forEach(function (card) {
      card.classList.remove('flavor_active');
    });
    clickedCard.classList.add('flavor_active');

    var currentNameEl = root.querySelector('.pdp-flavor-current-name');
    if (currentNameEl && flavorName) currentNameEl.textContent = flavorName;
  }

  document.addEventListener('click', function (event) {
    var card = closestFlavorLink(event.target);
    if (!card) return;
    // Let modified clicks (open in new tab, etc.) behave natively.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (card.classList.contains('flavor_active')) {
      event.preventDefault();
      return;
    }

    var sectionScope = card.closest('[data-section]');
    if (!sectionScope) return;
    var sectionId = sectionScope.dataset.section;
    // `[data-section]` also matches the inner <product-info>, which doesn't
    // contain the gallery — walk up to the actual section root so selectors
    // like .pdp-gallery resolve.
    var root = document.getElementById('MainProduct-' + sectionId);
    if (!root) return;
    var targetUrl = card.getAttribute('href');
    if (!targetUrl) return;

    event.preventDefault();
    setLoading(root, true);

    fetch(targetUrl + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (response) {
        if (!response.ok) throw new Error('Flavor fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var fetchedDoc = new DOMParser().parseFromString(html, 'text/html');
        var fetchedRoot = fetchedDoc.getElementById('MainProduct-' + sectionId);
        if (!fetchedRoot) throw new Error('Flavor fetch missing section root');

        patchSection(root, fetchedRoot, sectionId);
        // The gallery's click listeners (thumbnail select, prev/next arrows)
        // were destroyed along with the old nodes when .pdp-gallery's
        // innerHTML got replaced above — rebind them on the fresh markup.
        if (typeof window.pdpInitGallery === 'function') {
          window.pdpInitGallery(root.querySelector('.pdp-gallery'));
        }
        setActiveCard(root, card, card.dataset.flavorName);
        // #add-to-cart-copy only had its price data attributes synced above
        // (its text is JS-owned); re-derive "Subscribe · $X" / "Add to cart ·
        // $X" from the freshly-synced attributes and whichever payment method
        // is currently selected.
        if (typeof window.updateAddToCartCopy === 'function') {
          var signatureRadio = root.querySelector('#payment-method-signature');
          window.updateAddToCartCopy(!!(signatureRadio && signatureRadio.checked));
        }
        window.history.pushState({}, '', targetUrl);
        document.title = fetchedDoc.title || document.title;
        // The freshly-injected price markup starts in its default (non-gifts)
        // state; re-apply the gifts A/B toggle so it matches whatever this
        // visitor was already seeing.
        if (typeof applyTotalPriceWithGifts === 'function') applyTotalPriceWithGifts();
        document.dispatchEvent(new CustomEvent('pdp:flavor-switched', { detail: { url: targetUrl } }));
      })
      .catch(function (error) {
        console.error(error);
        window.location.href = targetUrl;
      })
      .finally(function () {
        setLoading(root, false);
      });
  });
})();
